import React, { useCallback, useMemo, useState } from "react";

import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { WidgetTaskSingleSettingsScreenComponent } from "./WidgetTaskSingleSettingsScreenComponent";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { WSTaskSingleQuery } from "./__generated__/WSTaskSingleQuery.graphql";
import { WidgetTaskSinglePreview } from "./WidgetTaskSinglePreview";
import { WSTaskSingleMutation } from "./__generated__/WSTaskSingleMutation.graphql";
import { WAFakeHeader } from "../../WidgetAdd/WAFakeHeader";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { WidgetScreenPreview } from "../WidgetScreenPreview/WidgetScreenPreview";
import { WidgetScreen } from "../../common/WidgetScreen";

type Props = UserLoggedInNavigatorScreenProps<"WSTaskSingle">;
export const WSTaskSingle = withWebModal(WSTaskSingleC, true);
function WSTaskSingleC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWSTaskSingle {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWSTaskSingle(props: Props & { fetchKey: string }) {
  const intl_tokens = useIntlTokens();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const data = useLazyLoadQuery<WSTaskSingleQuery>(
    graphql`
      query WSTaskSingleQuery($id: ID!) {
        widgetTaskSingle(id: $id) {
          id
          task {
            id
            activeSectionVersions {
              id
            }
            routine {
              type
            }
            ...WidgetTaskSingleSettingsScreenComponent_task
          }
          activeSectionVersions {
            id
            section {
              id
            }
          }
        }
      }
    `,
    {
      id: props.route.params.widgetId,
    },
    { fetchKey: props.fetchKey }
  );

  const [sectionIds, setSectionIds] = useState<string[]>(
    data.widgetTaskSingle!.activeSectionVersions.length ===
      data.widgetTaskSingle!.task!.activeSectionVersions.length
      ? []
      : (data.widgetTaskSingle!.activeSectionVersions.map(
          ({ section }) => section.id
        ) ?? [])
  );
  const taskId = useMemo(() => {
    return data.widgetTaskSingle?.task?.id ?? "";
  }, [data.widgetTaskSingle?.task?.id]);

  const [save, loading] = useMutation<WSTaskSingleMutation>(graphql`
    mutation WSTaskSingleMutation(
      $storecastId: ID!
      $id: ID!
      $update: WidgetTaskSingleUpdateInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        updateWidgetTaskSingle(id: $id, update: $update) {
          id
        }
      }
    }
  `);

  const onSave = useCallback(() => {
    save({
      variables: {
        id: props.route.params.widgetId,
        storecastId: props.route.params.storecastId,
        update: {
          setSectionIds: sectionIds,
        },
      },
      onCompleted: props.navigation.goBack,
    });
  }, [
    props.navigation.goBack,
    props.route.params.storecastId,
    props.route.params.widgetId,
    save,
    sectionIds,
  ]);

  return (
    <WidgetScreen
      preview={
        <WidgetScreenPreview
          onSave={onSave}
          size={props.route.params.size}
          unitSize={props.route.params.unitSize}
          loading={loading}
        >
          {data.widgetTaskSingle?.task?.id ? (
            <WidgetTaskSinglePreview
              sectionIds={sectionIds}
              storecastId={props.route.params.storecastId}
              taskId={data.widgetTaskSingle.task.id}
              clientForceSize={
                props.route.params.size.w === 1 ? "ONE_X_ONE" : "TWO_X_ONE"
              }
            />
          ) : null}
        </WidgetScreenPreview>
      }
      settings={
        <>
          {isCompact ? null : (
            <WAFakeHeader
              title={
                data.widgetTaskSingle?.task?.routine.type === "KPI"
                  ? intl_tokens.SelectMeasurementAreas
                  : ""
              }
            />
          )}
          <WidgetTaskSingleSettingsScreenComponent
            task={data.widgetTaskSingle!.task!}
            sectionIds={sectionIds}
            setSectionIds={setSectionIds}
            storecastId={props.route.params.storecastId}
            taskId={taskId}
          />
        </>
      }
    />
  );
}
